/**
 * "Universal Section" control — compare top bar, left of the shortlist.
 *
 * One click picks a section in the universal (US-based) vocabulary; every
 * compare panel then resolves it to ITS OWN region — §5 in a USPI, §4.4 in an
 * EU SmPC, §7 in a Health Canada PM — filtering a section-text panel and
 * scrolling a PDF panel.
 *
 * Renders the vocabulary `/api/section-concepts` serves and holds no copy of
 * the universal→region map: adding a region or an 18th section is a backend
 * seed change. See docs/plans/2026-09-22-universal-section-selector.md.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Layers, X } from 'lucide-react';

import { universalVocabulary, type ConceptEntry } from '../utils/universalSections';

interface Props {
  concepts: ConceptEntry[];
  /** Selected universal-section keys (multi-select). */
  values: string[];
  onChange: (keys: string[]) => void;
}

export default function UniversalSectionSelect({ concepts, values, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const vocab = universalVocabulary(concepts);
  // The endpoint is reference data that loads once; until it does there is
  // nothing to offer, and an empty menu is worse than no control.
  if (vocab.length === 0) return null;

  const selectedSet = new Set(values);
  const selected = vocab.filter((c) => selectedSet.has(c.key));
  const active = selected[0] ?? null; // first pick drives the collapsed-button label
  // Toggle one key in/out, preserving vocabulary order so the URL + filter are
  // stable regardless of click order.
  const toggle = (key: string) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(vocab.filter((c) => next.has(c.key)).map((c) => c.key));
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="flex items-center gap-1">
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="Universal Section"
          aria-expanded={open}
          className={`inline-flex h-9 items-center gap-1.5 rounded-ctl border px-3 text-sm font-medium transition-colors ${
            active
              ? 'border-accent-ring bg-accent-tint text-accent-deep'
              : 'border-line-input text-ink-2 hover:bg-panel'
          }`}
          title="Pick one or more sections — every panel narrows to its own region's equivalent"
        >
          <Layers className="h-4 w-4" />
          {active ? (
            <>
              <span aria-hidden className="tabular-nums opacity-60">{active.universal_order}</span>
              {active.label}
              {selected.length > 1 && (
                <span aria-hidden className="opacity-60">{` +${selected.length - 1}`}</span>
              )}
            </>
          ) : (
            'Universal Section'
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
        {selected.length > 0 && (
          <button
            onClick={() => onChange([])}
            aria-label="Clear universal sections"
            title="Clear — every panel back to all sections"
            className="rounded p-1 text-ink-3 hover:bg-panel"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1 max-h-[70vh] w-80 overflow-y-auto rounded border border-line bg-white py-1 shadow-pop"
        >
          {vocab.map((c) => (
            <button
              key={c.key}
              role="option"
              aria-selected={selectedSet.has(c.key)}
              aria-label={c.label}
              onClick={() => toggle(c.key)}
              className={`block w-full px-3 py-1.5 text-left text-sm ${
                selectedSet.has(c.key) ? 'bg-accent-tint font-medium text-accent-deep' : 'text-ink hover:bg-panel'
              }`}
            >
              {/* The universal number (0-16) from the owner's mapping sheet —
                  a row identifier, NOT a section number in any region. */}
              <span
                aria-hidden
                className="mr-2 inline-block w-5 text-right text-xs tabular-nums text-ink-3"
              >
                {c.universal_order}
              </span>
              {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
